import { getCallDiagnostics } from '@/services/logger/call-diagnostics';
import { createLogger } from '@/services/logger/logger';
import { tracingService } from '@/services/tracing/tracing-service';
import { CallEventLevel, CallEventName } from '@/services/tracing/types';
import { useCallback, useEffect } from 'react';
import { useVonageClient } from '../../vonage-client-provider';
import { serializeError } from '../error-utils';
import { useVoiceCallState } from '../voice-call-container';
import { withRetry } from '../with-retry';

const log = createLogger('Mute', ['calls']);

/**
 * @internal
 * This hook is used internally by VoiceCallActionsProvider.
 * Consumers should use useVoiceCallActions() instead.
 */
export const useMute = () => {
  const vonageClient = useVonageClient();
  const [call, callDispatch] = useVoiceCallState();

  useEffect(() => {
    const handleMute = vonageClient.on('mute', (callId, legId, muted) => {
      if (callId !== legId) {
        return;
      }
      if (call.callId !== callId) {
        return;
      }
      callDispatch({ type: 'call:mute:updated', muted });
    });

    return () => {
      vonageClient.off('mute', handleMute);
    };
  }, [callDispatch, call.callId, vonageClient]);

  const toggleMute = useCallback(async () => {
    const callId = call.callId;
    if (!callId) {
      return;
    }
    if (call.callState === 'idle' || call.callState === 'hangingup') {
      return;
    }

    const diagnostics = await getCallDiagnostics();

    log.info('Mute toggle attempt', {
      callId,
      muted: call.muted,
      timestamp: Date.now(),
      diagnostics,
    });
    tracingService.trackEvent(CallEventName.AttemptMute, CallEventLevel.Info, {
      deviceLegId: callId,
      muted: call.muted,
    });

    try {
      await withRetry(
        async () => {
          if (call.muted) {
            await vonageClient.unmute(callId);
          } else {
            await vonageClient.mute(callId);
          }
        },
        {
          name: 'toggleMute',
          maxRetries: 1,
          // Mute/unmute retries do not refresh the session. `withRetry`
          // retries the mute/unmute once on failure; if that also fails,
          // `onFailure` below logs the outcome and Sentry captures the error
          // via `sentry.tag`.
          sentry: {
            tag: 'vonage_toggle_mute_error',
            extra: {
              callId,
            },
          },
          onFailure: (error: unknown) => {
            log.error('Mute toggle failed', {
              callId,
              muted: call.muted,
              ...serializeError(error),
              timestamp: Date.now(),
              diagnostics,
            });
          },
        },
      );
    } catch (_error) {
      // Already logged in onFailure
    }
  }, [call.callId, call.callState, call.muted, vonageClient]);

  return { isMuted: call.muted, toggleMute };
};
